import { useInkind } from '@gik/api/inkinds/inkind';
import { auth, SignupForm } from '@gik/auth';
import ExternalLoginForm from '@gik/auth/components/ExternalLogin/ExternalLoginForm';
import { signupFormId } from '@gik/auth/components/Signup/SignupForm';
import { performLogin } from '@gik/auth/utils/auth';
import type UserRegistrationData from '@gik/core/models/gik/auth/UserRegistrationData';
import { useBemCN } from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { useInvitationEmailAddressFromToken } from '@gik/finish-registration/api/FinishRegistrationAPI';
import { finishRegistrationSearchParam, signupEmailSearchParam } from '@gik/finish-registration/consts';
import { shouldShowPagePicture } from '@gik/finish-registration/utils';
import { PageAvatar } from '@gik/ui/Avatar';
import { Button } from '@gik/ui/Button';
import { PageSection } from '@gik/ui/gik/PageSection';
import { Input } from '@gik/ui/Input';
import { SvgIcon } from '@gik/ui/SvgIcon/SvgIcon';
import { UI } from '@gik/ui/UIManager';
import InformationCircleIcon from '@heroicons/react/outline/InformationCircleIcon';
import React from 'react';
import useSearchParam from 'react-use/lib/useSearchParam';

export interface IFinishRegistrationProps {
  routeId: string;
  buttonsPortal?: React.MutableRefObject<HTMLDivElement>;
}

// todo i18n
export default function FinishRegistrationComp({ routeId, buttonsPortal }: IFinishRegistrationProps) {
  const bem = useBemCN('finish-registration');
  const invitationToken = useSearchParam(finishRegistrationSearchParam);
  const { data: { email: invitationEmailAddress } = { invitationEmailAddress: undefined } } =
    useInvitationEmailAddressFromToken(invitationToken);
  const { data: inkindPage } = useInkind(routeId);

  if (!invitationEmailAddress) {
    // invitationEmailAddress is required to populate the form
    return null;
  }

  function handleSuccess(userId, refreshToken, refreshTokenHash, walletRefreshToken, redirectUrl) {
    const url = new URL(window.location.toString());
    url.searchParams.delete(signupEmailSearchParam);
    window.history.pushState({}, '', url.toString());

    performLogin(userId, refreshToken, refreshTokenHash, walletRefreshToken, redirectUrl);
    UI.closeDialog();
  }

  async function onFinishRegistrationSubmit(formValues) {
    const userData: UserRegistrationData = {
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      password: formValues.password,
      emailAddress: formValues.email,
      inkindRouteId: inkindPage.routeId,
    };

    const { userId, refreshToken, refreshTokenHash, redirectUrl } = await auth.executeUserSignUp(userData);

    if (userId) {
      handleSuccess(userId, refreshToken, refreshTokenHash, undefined, redirectUrl);
    }
  }

  function buttons(valid: boolean, submitting: boolean) {
    return (
      <div {...bem('finish-signup-button')}>
        <Button
          variant={'primary'}
          type="submit"
          form={signupFormId}
          disabled={submitting || !valid}
          loading={submitting}
        >
          Finish sign-up
        </Button>
      </div>
    );
  }

  return (
    <div {...bem('wrapper')}>
      <section {...bem('page-info')}>
        <PageSection variant={'aqua'} {...bem('instructions')}>
          <div {...bem('instructions-wrapper')}>
            {shouldShowPagePicture(inkindPage?.thumb_medium) && (
              <div>
                <PageAvatar
                  variant={'primary'}
                  circle={false}
                  showBorder
                  size={'2xl'}
                  imageUrl={inkindPage?.thumb_medium}
                />
              </div>
            )}
            <div {...bem('instructions-copy')}>
              <div {...bem('title')}>Welcome to Give InKind!</div>
              <div {...bem('subtitle')}>
                Finish the quick registration below so you can start helping{' '}
                <span {...bem('page-title')}>{inkindPage?.title}</span>
              </div>
            </div>
          </div>
        </PageSection>
      </section>

      <ExternalLoginForm
        onSuccess={({ userId, refreshToken, refreshTokenHash, walletAccessToken, redirectUrl }) =>
          handleSuccess(userId, refreshToken, refreshTokenHash, walletAccessToken, redirectUrl)
        }
      />

      <p {...bem('or-via-email')}>…or sign-up using email:</p>

      <div {...bem('form-wrapper')}>
        <section {...bem('email-address-section')}>
          <Input
            disabled
            value={invitationEmailAddress}
            append={
              <Button
                circle
                variant={'primary-plain'}
                tooltip={
                  'To use a different email, please ask the Organizer to resend the invitation to that email address.'
                }
              >
                <SvgIcon Icon={InformationCircleIcon} />
              </Button>
            }
          />
        </section>

        <SignupForm
          {...bem('sign-up-form')}
          hideEmailField
          initialValues={{
            email: invitationEmailAddress,
          }}
          buttonsOverride={buttons}
          buttonsPortal={() => buttonsPortal?.current}
          onSubmitOverride={onFinishRegistrationSubmit}
        />
      </div>
    </div>
  );
}

export const FinishRegistration = withComponentErrorBoundary(FinishRegistrationComp);
